import { format } from 'date-fns'
import { useAtomValue } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { incommingChatMessageAtom } from '../../../store/atoms'
import type { EEvent, EHandName } from '../../../types/enums'
import { trpc } from '../../../utils'
import { Scrollable } from '../../atoms'
import {
  EventLogItem,
  SendMessageInput,
  TextMessageEntity,
} from '../../molecules'
import * as style from './index.css'

export const Chat = (props: {
  tableId: string
  userId?: string
  canSend: boolean
}) => {
  const { data } = trpc.chat.getHistory.useQuery({
    tableId: props.tableId,
  })
  const [chatHistory, setChatHistory] = useState<
    | {
        name: string | null
        cards: string | null
        bet: number
        table_id: string
        user_id: string
        event_date: string
        event: EEvent
        community_cards: string | null
        hand_number: number
        hand_name: EHandName
        bank: number
        timestamp: string
      }[]
    | {
        id: string
        userId: string
        text: string
        name: string
        avatar: string | null
        timestamp: Date
      }[]
  >()
  const incommingMessage = useAtomValue(incommingChatMessageAtom)

  const {
    mutate: sendMessage,
    isPending: isSendPending,
    isSuccess: isSendSuccess,
  } = trpc.chat.sendMessage.useMutation()

  const [message, setMessage] = useState('')
  const lastMinuteRef = useRef<string | null>(null)
  const lastMessageUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (isSendSuccess) {
      setMessage('')
    }
  }, [isSendSuccess])

  useEffect(() => {
    if (incommingMessage) {
      // @ts-expect-error timestamp number os string...
      setChatHistory((prev) => {
        if (prev) {
          return [...prev, incommingMessage]
        }
        return [incommingMessage]
      })
    }
  }, [incommingMessage])

  useEffect(() => {
    if (data?.messages) {
      // @ts-expect-error type bullshit
      setChatHistory(data?.messages)
    }
  }, [data])

  return (
    <div className={style.chat}>
      <div className={style.content}>
        <Scrollable
          className={style.scrollable}
          initScrollPosition='bottom'
        >
          {chatHistory
            ? chatHistory.map((item, idx) => {
                const date = new Date(item.timestamp)
                const dateValue = format(date, 'HH:mm')

                const showTimestamp = lastMinuteRef.current !== dateValue
                if (showTimestamp) {
                  lastMinuteRef.current = dateValue
                }

                const showAvatar =
                  'userId' in item &&
                  lastMessageUserIdRef.current !== item.userId

                lastMessageUserIdRef.current =
                  'userId' in item ? item.userId : null

                if ('event' in item) {
                  return (
                    <div
                      key={`${idx}-${item.timestamp}`}
                      className={style.chatEntity}
                    >
                      {showTimestamp && (
                        <div className={style.timestamp}>{dateValue}</div>
                      )}
                      <EventLogItem
                        event={item.event}
                        timestamp={date}
                        handNumber={item.hand_number ?? 0}
                        handName={item.hand_name ?? ''}
                        bank={item.bank}
                        bet={item.bet}
                        cards={item.cards}
                        communityCards={item.community_cards}
                        name={item.name}
                      />
                    </div>
                  )
                }

                if (item.text) {
                  const isMine =
                    (item.userId && item.userId === props.userId) || false

                  return (
                    <div
                      key={item.id}
                      className={style.chatEntity}
                    >
                      {showTimestamp && (
                        <div className={style.timestamp}>{dateValue}</div>
                      )}
                      <TextMessageEntity
                        text={item.text}
                        isMine={isMine}
                        userAvatarUrl={item.avatar}
                        showAvatar={showAvatar}
                        arrowPosition={
                          showAvatar
                            ? {
                                vertical: 'bottom',
                                horizontal: isMine ? 'right' : 'left',
                              }
                            : null
                        }
                      />
                    </div>
                  )
                }

                return null
              })
            : null}
        </Scrollable>
      </div>

      {props.canSend ? (
        <SendMessageInput
          message={message}
          disabled={isSendPending}
          onUpdateMessage={(message) => setMessage(message || '')}
          onSendMessage={() =>
            sendMessage({
              tableId: props.tableId,
              text: message,
            })
          }
        />
      ) : null}
    </div>
  )
}
